import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText } from 'lucide-react';
import FileDropzone from '../../components/ui/FileDropzone';
import { outreachApi } from '../api/outreachApi';
import VerificationPanel from './VerificationPanel';

const TARGET_FIELDS = [
  'email',
  'first_name',
  'last_name',
  'company',
  'title',
  'website',
  'linkedin',
  'timezone',
  'country',
  'verification_status',
];

const POLL_INTERVAL_MS = 1000;

export default function ImportWizard({ lists, onDone, onCancel }) {
  const { t } = useTranslation('common');

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [listName, setListName] = useState('');
  const [listId, setListId] = useState('');
  const [tags, setTags] = useState('');
  const [dedupeAcrossLists, setDedupeAcrossLists] = useState(false);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const pickFile = async (selected) => {
    setFile(selected);
    setPreview(null);
    setError(null);
    setBusy(true);
    try {
      const data = await outreachApi.previewImport(selected);
      setPreview(data);
      setMapping(data.suggested_mapping);
      setListName(selected.name.replace(/\.csv$/i, ''));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const startImport = async () => {
    setBusy(true);
    setError(null);
    try {
      const started = await outreachApi.startImport({
        file,
        mapping,
        listName: listId ? undefined : listName,
        listId: listId || undefined,
        tags,
        dedupeAcrossLists,
      });
      setJob(started);
      pollRef.current = setInterval(async () => {
        const fresh = await outreachApi.getImportJob(started.id);
        setJob(fresh);
        if (fresh.status !== 'running') {
          clearInterval(pollRef.current);
          setBusy(false);
        }
      }, POLL_INTERVAL_MS);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setBusy(false);
    }
  };

  const mappedToEmail = Object.values(mapping).includes('email');
  const report = job?.report;

  return (
    <div className="import-wizard">
      <div className="import-wizard-head">
        <h2 className="unibox-detail-subject">{t('outreach.importCsv')}</h2>
        <button className="unibox-page-button" onClick={onCancel}>
          {t('outreach.close')}
        </button>
      </div>

      {!job && (
        <>
          <FileDropzone
            accept=".csv,text/csv"
            disabled={busy}
            title={t('outreach.importHelp.dropTitle')}
            hint={t('outreach.importHelp.dropHint')}
            onFiles={pickFile}
          >
            {file && (
              <div className="import-chosen">
                <FileText size={14} /> {t('outreach.importHelp.chosen', { name: file.name })}
              </div>
            )}
          </FileDropzone>

          <p className="import-columns">
            {t('outreach.importHelp.columns')}{' '}
            <a className="import-sample-link" href="/sample-contacts.csv" download>
              <Download size={13} /> {t('outreach.importHelp.sample')}
            </a>
          </p>

          {preview && (
            <>
              <p className="unibox-reply-hint">
                {t('outreach.detected', { encoding: preview.encoding, delimiter: preview.delimiter })}
              </p>

              <table className="admin-table import-mapping">
                <thead>
                  <tr>
                    <th>{t('outreach.column')}</th>
                    <th>{t('outreach.sampleValue')}</th>
                    <th>{t('outreach.mapsTo')}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.headers.map((header) => (
                    <tr key={header}>
                      <td>{header}</td>
                      <td className="import-sample">{(preview.sample_rows[0] || {})[header]}</td>
                      <td>
                        <select
                          className="unibox-select"
                          value={mapping[header] || ''}
                          onChange={(e) => {
                            const next = { ...mapping };
                            if (e.target.value) next[header] = e.target.value;
                            else delete next[header];
                            setMapping(next);
                          }}
                        >
                          <option value="">{t('outreach.asCustomField')}</option>
                          {TARGET_FIELDS.map((field) => (
                            <option key={field} value={field}>
                              {field}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="unibox-reply-actions">
                <select className="unibox-select" value={listId} onChange={(e) => setListId(e.target.value)}>
                  <option value="">{t('outreach.newList')}</option>
                  {lists.map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.name}
                    </option>
                  ))}
                </select>
                {!listId && (
                  <input
                    className="unibox-select"
                    placeholder={t('outreach.listName')}
                    value={listName}
                    onChange={(e) => setListName(e.target.value)}
                  />
                )}
                <input
                  className="unibox-select"
                  placeholder={t('outreach.tagsPlaceholder')}
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                />
                <label className="campaign-checkbox">
                  <input
                    type="checkbox"
                    checked={dedupeAcrossLists}
                    onChange={(e) => setDedupeAcrossLists(e.target.checked)}
                  />
                  {t('outreach.dedupeAcrossLists')}
                </label>
                <button className="unibox-button" onClick={startImport} disabled={busy || !mappedToEmail}>
                  {t('outreach.startImport')}
                </button>
                {!mappedToEmail && <span className="unibox-error">{t('outreach.mapEmailFirst')}</span>}
              </div>
            </>
          )}
        </>
      )}

      {job && (
        <div className="import-report">
          <p>
            <strong>{t(`outreach.importStatus.${job.status}`, job.status)}</strong>
            {job.status === 'running' && ` — ${t('outreach.importedSoFar', { count: report.created })}`}
          </p>
          {job.error && <div className="unibox-error">{job.error}</div>}
          {report && (
            <table className="admin-table">
              <tbody>
                <tr><td>{t('outreach.report.totalRows')}</td><td>{report.total_rows}</td></tr>
                <tr><td>{t('outreach.report.created')}</td><td>{report.created}</td></tr>
                <tr><td>{t('outreach.report.updated')}</td><td>{report.updated}</td></tr>
                <tr><td>{t('outreach.report.duplicates')}</td><td>{report.duplicates_in_file}</td></tr>
                {dedupeAcrossLists && (
                  <tr><td>{t('outreach.report.crossListDuplicates')}</td><td>{report.cross_list_duplicates}</td></tr>
                )}
                <tr><td>{t('outreach.report.suppressed')}</td><td>{report.suppressed}</td></tr>
                <tr><td>{t('outreach.report.invalid')}</td><td>{report.invalid_syntax}</td></tr>
              </tbody>
            </table>
          )}
          {report?.rejected_samples?.length > 0 && (
            <details>
              <summary>{t('outreach.report.rejectedSamples')}</summary>
              <ul>
                {report.rejected_samples.slice(0, 20).map((row) => (
                  <li key={`${row.row_number}-${row.value}`}>
                    {t('outreach.report.row')} {row.row_number}: {row.value || '—'} — {row.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {job.status === 'completed' && report.created + report.updated > 0 && (
            <>
              <p className="unibox-reply-hint">{t('outreach.verifyAfterImport')}</p>
              <VerificationPanel listId={job.list_id} />
            </>
          )}

          {job.status !== 'running' && (
            <button className="unibox-button" onClick={onDone}>
              {t('outreach.done')}
            </button>
          )}
        </div>
      )}

      {error && <div className="unibox-error">{String(error)}</div>}
    </div>
  );
}
